import { GAME_NAME, GAME_TAGLINE, INK, SPACE, TYPE } from "@gaoya/shared";
import type { AssetBank } from "./assets.js";
import { img } from "./assets.js";
import type { BtnKind, UiButton } from "../ui/hall.js";
import type { UiState } from "../game/state.js";

export function hash(n: number, salt: number): number {
  const x = Math.sin(n * 0.0013 + salt * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

export function roundPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export function fillRound(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string,
): void {
  ctx.fillStyle = fill;
  roundPath(ctx, x, y, w, h, r);
  ctx.fill();
}

/** 贴底托盘：只圆上方两角，避免底排键压在圆角上。 */
export function roundPathTop(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x, y + h);
  ctx.lineTo(x, y + rr);
  ctx.arcTo(x, y, x + rr, y, rr);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h);
  ctx.closePath();
}

export function fillRoundTop(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string,
): void {
  ctx.fillStyle = fill;
  roundPathTop(ctx, x, y, w, h, r);
  ctx.fill();
}

export function strokeRound(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  stroke: string,
  lw = 1.2,
): void {
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lw;
  roundPath(ctx, x, y, w, h, r);
  ctx.stroke();
}

export function hairline(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, color = INK.rule): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}

/** 新中式配色：宣纸底、松烟墨、朱砂印、赭金。微信端不画阴影，层次靠描边与底边“唇”。 */
export const C = {
  paper: "#F6EFE2",
  paperDeep: "#E9DEC9",
  card: "#FFFCF5",
  cardSoft: "#FAF4E9",
  well: "#F1E8D8",
  line: "rgba(92,62,32,0.12)",
  lineStrong: "rgba(92,62,32,0.22)",
  ink: "#2A211B",
  ink2: "#6E5E51",
  ink3: "#9E8D7E",
  seal: "#B83A24",
  sealLight: "#CC4A30",
  sealDeep: "#8C2616",
  sealSoft: "#F6E2DA",
  gold: "#D2A04A",
  goldLight: "#E4B95F",
  goldDeep: "#8A6220",
  goldSoft: "#F5E7C8",
  jade: "#3D7A5F",
  jadeSoft: "#E2EEE6",
  night: "#2A2420",
  nightRaised: "#3A322C",
} as const;

let frameAssets: AssetBank | null = null;
export function setFrameAssets(a: AssetBank): void {
  frameAssets = a;
}
export function art(name: string): CanvasImageSource | null {
  return frameAssets ? img(frameAssets, name) : null;
}

/** 绘制图片（contain），底边对齐；返回实际绘制宽度。 */
export function drawArt(
  ctx: CanvasRenderingContext2D,
  name: string,
  x: number,
  y: number,
  w: number,
  h: number,
  alignBottom = true,
): number {
  const im = art(name);
  if (!im) return 0;
  const size = imageExtent(im);
  if (!size) return 0;
  const s = Math.min(w / size.w, h / size.h);
  const dw = size.w * s;
  const dh = size.h * s;
  ctx.drawImage(im, x + (w - dw) / 2, alignBottom ? y + h - dh : y + (h - dh) / 2, dw, dh);
  return dw;
}

/** 卡片：实底 + 发丝描边 + 底唇。 */
export function drawPanel(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r = 14,
  fill: string = C.card,
  border: string | null = C.line,
  lip: string | null = "rgba(92,62,32,0.08)",
): void {
  if (lip) fillRound(ctx, x, y + 2, w, h, r, lip);
  fillRound(ctx, x, y, w, h, r, fill);
  if (border) {
    ctx.strokeStyle = border;
    ctx.lineWidth = 1;
    roundPath(ctx, x + 0.5, y + 0.5, w - 1, h - 1, Math.max(0, r - 0.5));
    ctx.stroke();
  }
}

export function drawCard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  fill: string = C.card,
  r: number = SPACE.radius,
): void {
  drawPanel(ctx, x, y, w, h, r, fill);
}

/** 方印：朱底白字，内细框。 */
export function drawSeal(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  text: string,
  color: string = C.seal,
  ink = "#FFF7EE",
  rot = 0,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  if (rot) ctx.rotate(rot);
  const s = size;
  fillRound(ctx, -s / 2, -s / 2, s, s, s * 0.16, color);
  ctx.strokeStyle = "rgba(255,247,238,0.55)";
  ctx.lineWidth = Math.max(1, s * 0.03);
  roundPath(ctx, -s / 2 + s * 0.09, -s / 2 + s * 0.09, s * 0.82, s * 0.82, s * 0.1);
  ctx.stroke();
  ctx.fillStyle = ink;
  const chars = [...text];
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if (chars.length <= 1) {
    ctx.font = `700 ${Math.round(s * 0.52)}px ${TYPE.serif}`;
    ctx.fillText(text, 0, s * 0.03);
  } else {
    ctx.font = `700 ${Math.round(s * 0.34)}px ${TYPE.serif}`;
    const half = Math.ceil(chars.length / 2);
    const cols = [chars.slice(0, half), chars.slice(half)];
    // 竖排右起
    for (let c = 0; c < cols.length; c++) {
      const col = cols[c] ?? [];
      for (let i = 0; i < col.length; i++) {
        ctx.fillText(col[i] ?? "", (c === 0 ? 1 : -1) * s * 0.19, (i - (col.length - 1) / 2) * s * 0.36 + s * 0.02);
      }
    }
  }
  ctx.restore();
}

/** 小标签胶囊。 */
export function drawTag(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  h: number,
  fill: string,
  ink: string,
  align: "left" | "right" | "center" = "left",
  size = 11,
): number {
  ctx.font = `700 ${size}px ${TYPE.sans}`;
  const w = ctx.measureText(text).width + h * 0.9;
  const x0 = align === "left" ? x : align === "right" ? x - w : x - w / 2;
  fillRound(ctx, x0, y, w, h, h / 2, fill);
  ctx.fillStyle = ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x0 + w / 2, y + h / 2 + 0.5);
  return w;
}

/** 区块小标题：朱砂竖条 + 宋体。 */
export function sectionTitle(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  right?: string,
  rightX?: number,
): void {
  fillRound(ctx, x, y - 8, 3, 16, 1.5, C.seal);
  ctx.fillStyle = C.ink;
  ctx.font = `700 16px ${TYPE.serif}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + 11, y);
  if (right && rightX !== undefined) {
    ctx.fillStyle = C.ink2;
    ctx.font = `12px ${TYPE.sans}`;
    ctx.textAlign = "right";
    ctx.fillText(right, rightX, y);
  }
}

/** 退格图标，避免字体缺 ⌫ 字形。 */
export function drawBackspaceIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, color: string): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1.5, s * 0.09);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  const w = s;
  const h = s * 0.68;
  const x0 = cx - w / 2;
  const y0 = cy - h / 2;
  ctx.beginPath();
  ctx.moveTo(x0 + w * 0.3, y0);
  ctx.lineTo(x0 + w, y0);
  ctx.lineTo(x0 + w, y0 + h);
  ctx.lineTo(x0 + w * 0.3, y0 + h);
  ctx.lineTo(x0, cy);
  ctx.closePath();
  ctx.stroke();
  const mx = x0 + w * 0.64;
  const d = h * 0.2;
  ctx.beginPath();
  ctx.moveTo(mx - d, cy - d);
  ctx.lineTo(mx + d, cy + d);
  ctx.moveTo(mx + d, cy - d);
  ctx.lineTo(mx - d, cy + d);
  ctx.stroke();
  ctx.restore();
}

export function drawGearIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, color: string): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.fillStyle = color;
  ctx.beginPath();
  const teeth = 8;
  for (let i = 0; i < teeth * 2; i++) {
    const a = (i / (teeth * 2)) * Math.PI * 2;
    const rr = i % 2 === 0 ? r : r * 0.78;
    const a1 = a - Math.PI / (teeth * 2) * 0.55;
    const a2 = a + Math.PI / (teeth * 2) * 0.55;
    ctx.lineTo(Math.cos(a1) * rr, Math.sin(a1) * rr);
    ctx.lineTo(Math.cos(a2) * rr, Math.sin(a2) * rr);
  }
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = C.card;
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.36, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

export function clipName(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let s = text;
  while (s.length > 1 && ctx.measureText(`${s}…`).width > maxW) s = s.slice(0, -1);
  return `${s}…`;
}

export function drawProgressBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  t: number,
  critical = false,
): void {
  fillRound(ctx, x, y, w, h, h / 2, C.well);
  const tw = Math.max(0, Math.min(1, t)) * w;
  if (tw < 1) return;
  ctx.save();
  roundPath(ctx, x, y, w, h, h / 2);
  ctx.clip();
  const g = ctx.createLinearGradient(x, y, x + w, y);
  if (critical) {
    g.addColorStop(0, C.sealLight);
    g.addColorStop(1, C.sealDeep);
  } else {
    g.addColorStop(0, C.goldLight);
    g.addColorStop(1, C.seal);
  }
  ctx.fillStyle = g;
  roundPath(ctx, x, y, tw, h, h / 2);
  ctx.fill();
  ctx.restore();
}

function imageExtent(src: CanvasImageSource): { w: number; h: number } | null {
  const rec = src as { naturalWidth?: number; naturalHeight?: number; width?: number; height?: number };
  const w =
    typeof rec.naturalWidth === "number" && rec.naturalWidth > 0
      ? rec.naturalWidth
      : typeof rec.width === "number"
        ? rec.width
        : 0;
  const h =
    typeof rec.naturalHeight === "number" && rec.naturalHeight > 0
      ? rec.naturalHeight
      : typeof rec.height === "number"
        ? rec.height
        : 0;
  if (!(w > 0 && h > 0)) return null;
  return { w, h };
}

/** 茶馆图铺满画布（cover）：按较长边缩放，超出部分裁掉，避免底下露白或接缝。 */
function drawPaperImage(ctx: CanvasRenderingContext2D, bg: CanvasImageSource, w: number, h: number): void {
  const size = imageExtent(bg);
  if (!size) {
    ctx.drawImage(bg, 0, 0, w, h);
    return;
  }
  const scale = Math.max(w / size.w, h / size.h);
  const dw = size.w * scale;
  const dh = size.h * scale;
  ctx.drawImage(bg, (w - dw) / 2, (h - dh) / 2, dw, dh);
}

/** 茶馆实景压一层宣纸：上浅下深，保证文字可读且不发灰。 */
export function paperBg(ctx: CanvasRenderingContext2D, w: number, h: number, assets?: AssetBank): void {
  if (assets) setFrameAssets(assets);
  ctx.fillStyle = C.paper;
  ctx.fillRect(0, 0, w, h);
  const bg = assets ? img(assets, "bg") : null;
  if (bg) {
    drawPaperImage(ctx, bg, w, h);
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, "rgba(246,239,226,0.78)");
    g.addColorStop(0.35, "rgba(246,239,226,0.88)");
    g.addColorStop(1, "rgba(242,233,216,0.94)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  }
  ctx.fillStyle = "rgba(92,62,32,0.025)";
  for (let i = 0; i < 64; i++) {
    ctx.fillRect(hash(i * 17, 3) * w, hash(i * 29, 7) * h, 1.2, 1.2);
  }
}

/** 旧版窗棂装饰已移除，保留签名以兼容。 */
export function lattice(_ctx: CanvasRenderingContext2D, _w: number, _h: number, _capsuleLeft: number): void {}

export function letterSheet(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  drawPanel(ctx, x, y, w, h, SPACE.radius);
}

export function woodPanel(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r = 14): void {
  fillEnamel(ctx, x, y, w, h, r, ENAMEL.gamboge);
}

export type EnamelTone = {
  from: string;
  to: string;
  ink: string;
  base: string;
  border?: string;
};

/** 按键材质：上浅下深的实色面 + 深色底座（按下时下沉）。 */
export const ENAMEL = {
  rouge: { from: C.sealLight, to: "#AE3420", ink: "#FFFFFF", base: C.sealDeep },
  cinnabar: { from: C.sealLight, to: "#AE3420", ink: "#FFFFFF", base: C.sealDeep },
  gamboge: { from: "#EBC46E", to: "#D7A447", ink: "#4A2E0C", base: "#A87A2C" },
  porcelain: { from: "#FFFDF8", to: "#F6EEE1", ink: C.ink, base: "#DDCFB8", border: "rgba(92,62,32,0.14)" },
  inkstone: { from: "#4A413A", to: "#3C342E", ink: "#F4EDE2", base: "#1E1915" },
  jade: { from: "#4C8B6F", to: "#3B7259", ink: "#FFFFFF", base: "#285643" },
} as const satisfies Record<string, EnamelTone>;

const LIP = 3;

export function fillEnamel(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  tone: EnamelTone,
  pressed = false,
): { x: number; y: number; w: number; h: number } {
  const lip = Math.min(LIP, Math.max(2, h * 0.07));
  const fh = h - lip;
  const fy = y + (pressed ? lip : 0);
  const rr = Math.min(r, w / 2, fh / 2);
  if (!pressed) fillRound(ctx, x, y + lip, w, fh, rr, tone.base);
  ctx.save();
  roundPath(ctx, x, fy, w, fh, rr);
  ctx.clip();
  const g = ctx.createLinearGradient(x, fy, x, fy + fh);
  g.addColorStop(0, tone.from);
  g.addColorStop(1, tone.to);
  ctx.fillStyle = g;
  ctx.fillRect(x, fy, w, fh);
  ctx.fillStyle = "rgba(255,255,255,0.22)";
  ctx.fillRect(x, fy, w, 1);
  if (pressed) {
    ctx.fillStyle = "rgba(0,0,0,0.06)";
    ctx.fillRect(x, fy, w, fh);
  }
  ctx.restore();
  if (tone.border) {
    ctx.strokeStyle = tone.border;
    ctx.lineWidth = 1;
    roundPath(ctx, x + 0.5, fy + 0.5, w - 1, fh - 1, Math.max(0, rr - 0.5));
    ctx.stroke();
  }
  return { x, y: fy, w, h: fh };
}

export function enamelLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  cy: number,
  ink: string,
): void {
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = ink;
  ctx.fillText(text, cx, cy + 0.5);
}

export function drawButton(ctx: CanvasRenderingContext2D, b: UiButton, pressed: boolean): void {
  if (b.kind === "silent") return;
  if (b.kind === "text") {
    ctx.font = `600 14px ${TYPE.sans}`;
    ctx.fillStyle = pressed ? C.sealDeep : C.seal;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(clipName(ctx, b.label, b.w - 8), b.x + b.w / 2, b.y + b.h / 2);
    return;
  }
  const r = Math.min(b.h >= 44 ? 14 : 10, b.h / 2);
  const tone =
    b.kind === "primary" || b.kind === "danger"
      ? ENAMEL.rouge
      : b.kind === "wood"
        ? ENAMEL.gamboge
        : ENAMEL.porcelain;
  const face = fillEnamel(ctx, b.x, b.y, b.w, b.h, r, tone, pressed);
  if (!b.label) return;
  if (b.label === "⌫") {
    drawBackspaceIcon(ctx, face.x + face.w / 2, face.y + face.h / 2, Math.min(22, face.h * 0.5), tone.ink);
    return;
  }
  const big = face.h >= 44;
  const sub = b.sub && b.w >= 240 ? b.sub : "";
  const size = big ? (b.kind === "primary" ? 18 : 16) : Math.min(15, Math.max(12, Math.round(face.h * 0.36)));
  const serif = big && b.kind !== "ghost";
  ctx.font = `700 ${size}px ${serif ? TYPE.serif : TYPE.sans}`;
  const label = clipName(ctx, b.label, face.w - 16 - (sub ? 110 : 0));
  const cy = face.y + face.h / 2;
  if (sub) {
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = tone.ink;
    ctx.fillText(label, face.x + 20, cy + 0.5);
    ctx.font = `500 12px ${TYPE.sans}`;
    ctx.textAlign = "right";
    ctx.fillStyle = tone.ink === "#FFFFFF" ? "rgba(255,255,255,0.82)" : "rgba(74,46,12,0.75)";
    ctx.fillText(clipName(ctx, sub, 110), face.x + face.w - 20, cy + 0.5);
    return;
  }
  enamelLabel(ctx, label, face.x + face.w / 2, cy, tone.ink);
}

export function brandTitle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  y: number,
  size: number,
  extra?: string,
): number {
  ctx.fillStyle = C.seal;
  ctx.font = `700 ${size}px ${TYPE.serif}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(GAME_NAME, cx, y);
  const subSize = Math.max(12, Math.round(size * 0.38));
  ctx.fillStyle = C.ink2;
  ctx.font = `${subSize}px ${TYPE.sans}`;
  const subY = y + size * 0.78;
  ctx.fillText(extra || GAME_TAGLINE, cx, subY);
  return subY + subSize * 0.7;
}

export function brandAnchorY(
  inset: { safeTop: number; capsuleTop: number; capsuleHeight: number },
  h: number,
  ratio: number,
): number {
  const capTop = inset.capsuleTop > 0 ? inset.capsuleTop : Math.max(6, inset.safeTop);
  const capBottom = capTop + Math.max(inset.capsuleHeight, 28);
  return Math.max(h * ratio, capBottom + 22);
}

export function capsuleBox(
  ui: Pick<UiState, "capsuleLeft" | "capsuleTop" | "capsuleHeight" | "safeTop">,
  w: number,
): { top: number; h: number; bottom: number; left: number; cy: number } {
  const top = ui.capsuleTop > 0 ? ui.capsuleTop : Math.max(6, ui.safeTop);
  const h = ui.capsuleHeight > 8 ? ui.capsuleHeight : 32;
  const left = ui.capsuleLeft > 80 && ui.capsuleLeft < w - 24 ? ui.capsuleLeft : w - 96;
  return { top, h, bottom: top + h, left, cy: top + h / 2 };
}

/** 页眉：与胶囊同高，左侧宋体朱砂标题 + 小字副题。 */
export function pageHeader(
  ctx: CanvasRenderingContext2D,
  ui: Pick<UiState, "capsuleLeft" | "capsuleTop" | "capsuleHeight" | "safeTop">,
  w: number,
  titleText: string,
  subtitle: string,
): number {
  const cap = capsuleBox(ui, w);
  const maxW = Math.max(80, cap.left - 32);
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = C.ink;
  ctx.font = `700 20px ${TYPE.serif}`;
  ctx.fillText(clipName(ctx, titleText, maxW), 16, cap.cy - 7);
  ctx.fillStyle = C.ink2;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText(clipName(ctx, subtitle, maxW), 16, cap.cy + 13);
  return cap.bottom + 12;
}

export function title(ctx: CanvasRenderingContext2D, text: string, cx: number, y: number, size: number): void {
  ctx.fillStyle = C.ink;
  ctx.font = `700 ${size}px ${TYPE.serif}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, cx, y);
}

export function sub(ctx: CanvasRenderingContext2D, text: string, cx: number, y: number, size = 14, color: string = C.ink2): void {
  ctx.fillStyle = color;
  ctx.font = `${size}px ${TYPE.sans}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, cx, y);
}

export function drawCircleAvatar(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource | null,
  cx: number,
  cy: number,
  r: number,
  fallback: string,
  ring: string | null = C.card,
): void {
  if (ring) {
    ctx.beginPath();
    ctx.arc(cx, cy, r + 2, 0, Math.PI * 2);
    ctx.fillStyle = ring;
    ctx.fill();
  }
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  if (image) {
    ctx.drawImage(image, cx - r, cy - r, r * 2, r * 2);
  } else {
    const g = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
    g.addColorStop(0, "#F4E6CF");
    g.addColorStop(1, "#E6D2B2");
    ctx.fillStyle = g;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    ctx.fillStyle = C.seal;
    ctx.font = `700 ${Math.max(12, Math.round(r * 0.9))}px ${TYPE.serif}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText((fallback && [...fallback][0]) || "客", cx, cy + 1);
  }
  ctx.restore();
  ctx.beginPath();
  ctx.arc(cx, cy, r + (ring ? 2 : 0), 0, Math.PI * 2);
  ctx.strokeStyle = C.line;
  ctx.lineWidth = 1;
  ctx.stroke();
}

const NO_LINE_START = "，。、；：？！）》」』”’…·,.;:?!)";

export function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const lines: string[] = [];
  let line = "";
  for (const ch of text) {
    const test = line + ch;
    if (line && ctx.measureText(test).width > maxW && !NO_LINE_START.includes(ch)) {
      lines.push(line);
      line = ch;
    } else line = test;
  }
  if (line) lines.push(line);
  return lines.length > 0 ? lines : [""];
}

export function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
  lh: number,
): number {
  const lines = wrapLines(ctx, text, maxW);
  for (let i = 0; i < lines.length; i++) ctx.fillText(lines[i] ?? "", x, y + i * lh);
  return lines.length * lh;
}

export function isPressed(b: UiButton, down: boolean, x: number, y: number): boolean {
  return down && x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
}

export type { BtnKind };
